import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import useInlineEdit from './useInlineEdit'
import { formatYMD } from './dateHelpers'

/* ---- Per-field inline editor — uses shared useInlineEdit hook ---- */
export function RailField({
  label, value, displayValue,
  type = 'text', options,
  readOnly, readOnlyReason, locked, lockReason,
  onCommit
}) {
  const edit = useInlineEdit({ value: value ?? '', locked, onCommit })
  const { editing, draft, setDraft, pendingConfirm, inputRef,
          begin: rawBegin, cancel, attemptSave, confirmSave, handleKey } = edit
  const begin = () => { if (!readOnly) rawBegin() }

  const shown = displayValue ?? value
  const isEmpty = shown === undefined || shown === null || shown === ''

  if (!editing) {
    return (
      <>
        <span className="ws-rail-key">{label}</span>
        <span
          className={`ws-rail-val ${isEmpty ? 'ws-rail-val--missing' : ''} ${readOnly ? '' : 'ws-rail-val--editable'}`}
          onClick={begin}
          title={readOnly ? (readOnlyReason || 'Read-only — locked once shipment is in transit') : 'Click to edit'}
        >
          {isEmpty ? 'not set' : shown}
        </span>
      </>
    )
  }

  return (
    <>
      <span className="ws-rail-key">{label}</span>
      <span className="ws-rail-field-edit">
        {type === 'select' ? (
          <select ref={inputRef} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={handleKey}>
            {(options || []).map(o => (
              typeof o === 'string'
                ? <option key={o} value={o}>{o}</option>
                : <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ) : (
          <input
            ref={inputRef}
            type={type}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={handleKey}
          />
        )}
        {pendingConfirm && (
          <div className="ws-rail-warn">
            <div className="ws-rail-warn-title">Heads up</div>
            {lockReason || 'Saving will notify providers and may invalidate existing quotes.'}
          </div>
        )}
        <div className="ws-rail-field-actions">
          {pendingConfirm ? (
            <>
              <button className="ws-btn ws-btn--primary ws-btn--small" onClick={confirmSave}>Confirm & notify</button>
              <button className="ws-btn ws-btn--outline ws-btn--small" onClick={cancel}>Cancel</button>
            </>
          ) : (
            <>
              <button className="ws-btn ws-btn--primary ws-btn--small" onClick={attemptSave}>Save</button>
              <button className="ws-btn ws-btn--outline ws-btn--small" onClick={cancel}>Cancel</button>
            </>
          )}
        </div>
      </span>
    </>
  )
}

/* ---- Right rail section header ---- */
export function RailSection({ title, readOnly, children, collapsible, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen)
  const isOpen = collapsible ? open : true

  return (
    <div className={`ws-rail-section ${readOnly ? 'is-locked-readonly' : ''} ${collapsible ? 'is-collapsible' : ''}`}>
      <div
        className="ws-rail-section-head"
        onClick={collapsible ? () => setOpen(!open) : undefined}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
      >
        <div className="ws-rail-section-title">{title}</div>
        {collapsible && (
          <span className="ws-rail-section-chevron">
            {isOpen ? <ChevronUp size={14} strokeWidth={2} /> : <ChevronDown size={14} strokeWidth={2} />}
          </span>
        )}
      </div>
      {isOpen && children}
    </div>
  )
}

// Determine which sections need warnings or are read-only based on shipment status
function lockStateFor(shipment) {
  const status = shipment.status
  if (status === 'BOOKED') {
    return { route: 'readonly', cargo: 'readonly', commodity: 'readonly', commercial: 'readonly' }
  }
  const hasSentQuotes = (shipment.providers || []).some(p => p.status === 'received' || p.status === 'pending')
  if (
    status === 'RFQS_SENT' ||
    status === 'PARTIAL' ||
    status === 'DECIDE' ||
    status === 'NEEDS_REPLY' ||
    hasSentQuotes
  ) {
    return { route: 'locked', cargo: 'locked', commodity: 'locked', commercial: 'locked' }
  }
  return { route: 'open', cargo: 'open', commodity: 'open', commercial: 'open' }
}

const LOCK_REASONS = {
  route: 'Origin/destination/mode/ready-date changes will invalidate existing quotes — providers will be re-notified and asked to requote.',
  cargo: 'Pallet count, dims, and weight affect quoted cost. Saving will notify providers and may trigger a requote.',
  commodity: 'Commodity changes affect risk classification. Providers will be notified.',
  commercial: 'Incoterm changes shift which legs each provider quotes. Providers will be notified.'
}

const WARN_FIELDS = new Set([
  'route.originName', 'route.destName', 'route.mode', 'route.readyDate',
  'cargo.palletCount', 'cargo.dims', 'cargo.totalWeight',
  'commercial.incoterm'
])
const shouldWarn = (section, fieldKey) => WARN_FIELDS.has(`${section}.${fieldKey}`)

const INCOTERM_OPTIONS = ['EXW', 'FOB', 'FCA', 'CIF', 'CFR', 'DAP', 'DDP']
const MODE_OPTIONS = ['Ocean FCL', 'Ocean LCL', 'Air', 'Truck FTL', 'Truck LTL']

// A trucking load's mode is FTL or LTL, and it decides which carriers receive
// the RFQ. "Trucking" is the unset state and has to stay reachable: a mode
// nobody is sure of goes to every trucking carrier, which is the safe default,
// so this row must be able to go back to it.
const TRUCKING_MODE_OPTIONS = ['Trucking', 'Truck FTL', 'Truck LTL']

export default function ShipmentRail({ shipment, onFieldUpdate }) {
  const locks = lockStateFor(shipment)

  const commit = (section, fieldKey) => (newValue, { notify }) =>
    onFieldUpdate(section, fieldKey, newValue, { notify })

  const r = shipment.route
  const c = shipment.cargo
  const com = shipment.commercial || {}
  const cm = shipment.commodity || {}

  const fieldProps = (section, fieldKey) => ({
    locked: locks[section] === 'locked' && shouldWarn(section, fieldKey),
    readOnly: locks[section] === 'readonly',
    lockReason: LOCK_REASONS[section]
  })

  return (
    <aside className="ws-rail">
      <div className="ws-rail-head">
        <div className="ws-rail-title">Shipment</div>
        {shipment.trackingId && (
          <div className="ws-rail-id" title={shipment.id}>{shipment.trackingId}</div>
        )}
      </div>

      {/* ROUTE */}
      <RailSection title="Route" readOnly={locks.route === 'readonly'}>
        <div className="ws-rail-rows">
          <RailField
            label="Origin"
            value={r.origin.name}
            displayValue={`${r.origin.name} (${r.origin.code})`}
            onCommit={commit('route', 'originName')}
            {...fieldProps('route', 'originName')}
          />
          <RailField
            label="Destination"
            value={r.destination.name}
            displayValue={`${r.destination.name} (${r.destination.code})`}
            onCommit={commit('route', 'destName')}
            {...fieldProps('route', 'destName')}
          />
          <RailField
            label="Mode"
            type="select"
            options={shipment.modeKind === 'domestic' ? TRUCKING_MODE_OPTIONS : MODE_OPTIONS}
            value={shipment.mode}
            onCommit={commit('route', 'mode')}
            {...fieldProps('route', 'mode')}
            {...(shipment.modeKind === 'domestic' ? {} : {
              // No column behind an ocean mode label ("Ocean FCL", "Air"):
              // container_type holds '40HC' and the like, so a pick here had
              // nowhere honest to land.
              readOnly: true,
              readOnlyReason: 'Derived from the container type — not editable here'
            })}
          />
          <RailField
            label="Ready by"
            type="date"
            value={shipment.readyDate}
            displayValue={formatYMD(shipment.readyDate)}
            onCommit={commit('route', 'readyDate')}
            {...fieldProps('route', 'readyDate')}
          />
        </div>
      </RailSection>

      {/* CARGO */}
      <RailSection
        title="Cargo"
        collapsible
        defaultOpen={false}
        readOnly={locks.cargo === 'readonly'}
      >
        <div className="ws-rail-rows">
          <RailField
            label="Pallets"
            type="number"
            value={c.palletCount ?? ''}
            onCommit={commit('cargo', 'palletCount')}
            {...fieldProps('cargo', 'palletCount')}
          />
          <RailField
            label="Dimensions"
            value={c.dims}
            onCommit={commit('cargo', 'dims')}
            {...fieldProps('cargo', 'dims')}
          />
          <RailField
            label="Weight"
            type="number"
            value={c.totalWeight ?? ''}
            displayValue={c.totalWeight
              ? `${Number(c.totalWeight).toLocaleString()} ${c.totalWeightUnit || 'lb'}`
              : ''}
            onCommit={commit('cargo', 'totalWeight')}
            {...fieldProps('cargo', 'totalWeight')}
          />
          {c.totalVolume != null && (
            <>
              <span className="ws-rail-key">Volume</span>
              <span className="ws-rail-val">{c.totalVolume} ft³</span>
            </>
          )}
        </div>
      </RailSection>

      {/* TRADE */}
      <RailSection
        title="Trade"
        readOnly={locks.commodity === 'readonly' && locks.commercial === 'readonly'}
      >
        <div className="ws-rail-rows">
          <RailField
            label="Commodity"
            value={cm.description}
            onCommit={commit('commodity', 'description')}
            {...fieldProps('commodity', 'description')}
          />
          <RailField
            label="Incoterm"
            type="select"
            options={['', ...INCOTERM_OPTIONS]}
            value={com.incoterm || ''}
            onCommit={commit('commercial', 'incoterm')}
            {...fieldProps('commercial', 'incoterm')}
          />
        </div>
      </RailSection>

      {/* PROVIDERS */}
      <RailSection title="Providers">
        {(shipment.providers || []).length === 0 ? (
          <div className="ws-rail-val--missing" style={{ fontSize: 13 }}>No providers contacted yet</div>
        ) : (
          shipment.providers.map(p => {
            const statusClass =
              p.status === 'booked' || p.status === 'selected'
                ? 'is-booked'
                : p.status === 'declined'
                  ? 'is-declined'
                  : 'is-received'
            return (
              <div className={`ws-provider-row ${statusClass}`} key={p.name}>
                <span className="ws-provider-dot" aria-hidden="true" />
                <span className="ws-provider-name">
                  {p.name}
                </span>
                <span className="ws-provider-status">
                  {p.cost ? `$${p.cost.toLocaleString()}` : ''}
                  {p.cost && p.days ? ` · ${p.days}d` : ''}
                  {(p.cost || p.days) ? ' · ' : ''}
                  {p.status}
                </span>
              </div>
            )
          })
        )}
      </RailSection>

      {/* TIMELINE */}
      <RailSection title="Timeline" collapsible defaultOpen={false}>
        {(shipment.timeline || []).map((t, i) => (
          <div className="ws-timeline-row" key={i}>
            <span className="ws-timeline-date">{t.date}</span>
            <span className={`ws-timeline-event ${t.current ? 'is-current' : ''}`}>{t.label}</span>
          </div>
        ))}
      </RailSection>
    </aside>
  )
}
